import { useMemo } from "react";
import { useFromNumbers } from "./useFromNumbers";
import { useAuth } from "@tellescope/tanstack-query";

export type CallType = "Direct" | "Conference";

export interface DefaultCallSettings {
  /** Default "from" phone number for outbound calls */
  defaultFromNumber: string;
  /** Default call type based on organization settings */
  defaultCallType: CallType;
  /** Whether calls should be recorded (for conference calls) */
  recordCalls: boolean;
}

/**
 * Hook to get default call settings based on organization configuration.
 *
 * Default From Number Priority:
 * 1. Organization setting (settings.endusers.defaultPhoneNumber, unless "Personal Line")
 * 2. First available number from useFromNumbers
 * 3. User's personal twilioNumber
 * 4. Organization's twilioNumber
 *
 * Default Call Type:
 * - "Conference" if settings.endusers.defaultToOutboundConferenceCall is true
 * - "Direct" otherwise
 *
 * Record Calls:
 * - true if settings.endusers.recordCalls is true
 */
export function useDefaultCallSettings(): DefaultCallSettings {
  const { selectedOrganization: organization } = useAuth();

  const orgDefault = organization?.settings?.endusers?.defaultPhoneNumber;
  const preferredNumber = orgDefault && orgDefault !== "Personal Line" ? orgDefault : undefined;
  const { defaultFromNumber } = useFromNumbers({ preferredNumber });

  const defaultCallType = useMemo((): CallType => {
    return organization?.settings?.endusers?.defaultToOutboundConferenceCall ? "Conference" : "Direct";
  }, [organization?.settings?.endusers?.defaultToOutboundConferenceCall]);

  const recordCalls = useMemo(() => {
    return organization?.settings?.endusers?.recordCalls === true;
  }, [organization?.settings?.endusers?.recordCalls]);

  return { defaultFromNumber, defaultCallType, recordCalls };
}
